import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import { whatsappAccountService } from '../../services/api';
import {
    Box,
    Container,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    CircularProgress,
    Alert,
    IconButton,
    Link,
    Tabs,
    Tab,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip as MuiTooltip,
    Collapse,
    Skeleton
} from '@mui/material';
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppLayout from '../layout/AppLayout';
import {
    INK,
    MUTED,
    RULE,
    categoryMeta,
    pricingTypeMeta,
    prefersReducedMotion,
    formatCount,
    formatINR,
    formatPercent,
    groupTotals,
    Section,
    FigureStrip,
    CompositionBar,
    ChartTooltip,
    EmptyState,
    LoadingState,
    axisProps
} from '../analytics/analyticsUi';

// Quick ranges; calendar months match how usage is invoiced (browser time, i.e. IST for Indian users)
const PRESETS = [
    { key: 'this_month', label: 'This month' },
    { key: 'last_month', label: 'Last month' },
    { key: 'd7', label: '7 days', days: 7 },
    { key: 'd30', label: '30 days', days: 30 },
    { key: 'd90', label: '90 days', days: 90 }
];

const presetRange = (preset, now = Date.now()) => {
    const today = new Date(now);
    if (preset.key === 'this_month') {
        return { start: new Date(today.getFullYear(), today.getMonth(), 1).getTime(), end: new Date(now).setHours(23, 59, 59, 999) };
    }
    if (preset.key === 'last_month') {
        return {
            start: new Date(today.getFullYear(), today.getMonth() - 1, 1).getTime(),
            end: new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999).getTime()
        };
    }
    const end = new Date(now).setHours(23, 59, 59, 999);
    const start = new Date(end - (preset.days - 1) * 24 * 60 * 60 * 1000);
    start.setHours(0, 0, 0, 0);
    return { start: start.getTime(), end };
};

const TABS = ['messages', 'categories', 'pricing'];


const startOfDay = (ms) => {
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};
const endOfDay = (ms) => {
    const d = new Date(ms);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
};
const toInputValue = (ms) => {
    const d = new Date(ms);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fromInputValue = (value) => {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).getTime();
};

const periodLabel = (unixSeconds, granularity, long = false) => {
    if (!unixSeconds) return '';
    const date = new Date(unixSeconds * 1000);
    if (granularity === 'MONTH') {
        return date.toLocaleDateString('en-IN', { month: long ? 'long' : 'short', year: 'numeric' });
    }
    return date.toLocaleDateString('en-IN', long
        ? { day: 'numeric', month: 'short', year: 'numeric' }
        : { day: 'numeric', month: 'short' });
};

const getErrorMessage = (error) => {
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object') {
        if (error.message) return error.message;
        if (error.error) return typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
        return JSON.stringify(error);
    }
    return null;
};

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------
// Collapsible detail table
const DetailTable = ({ title, columns, rows }) => {
    const [open, setOpen] = useState(false);
    if (!rows.length) return null;
    return (
        <Section
            title={title}
            action={
                <Button
                    size="small"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    endIcon={<ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
                >
                    {open ? 'Hide rows' : `Show ${rows.length} rows`}
                </Button>
            }
            sx={{ pb: open ? 3 : 1 }}
        >
            <Collapse in={open} unmountOnExit>
                <TableContainer sx={{ maxHeight: 440 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.key}
                                        align={col.numeric ? 'right' : 'left'}
                                        sx={{ color: MUTED, fontWeight: 500, bgcolor: 'background.paper' }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow key={index} hover>
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            align={col.numeric ? 'right' : 'left'}
                                            sx={{ fontVariantNumeric: 'tabular-nums', color: INK }}
                                        >
                                            {col.render ? col.render(row) : row[col.key]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Collapse>
        </Section>
    );
};

// Explains where the cost comes from; admins also see Meta's cost, margin and missing rates
const CostNotice = ({ analytics }) => {
    const { user } = useContext(AuthContext);
    const isAdmin = user?.role === 'admin';
    if (analytics?.costAvailable !== false) return null;

    if (!analytics?.costEstimated) {
        return (
            <Alert severity="info" variant="outlined" sx={{ mb: 3, borderColor: RULE, color: INK }}>
                {analytics?.costUnavailableReason || 'Cost data is not available for this account.'} Message counts are still shown.
            </Alert>
        );
    }

    const estimate = analytics.estimate || {};
    const missing = estimate.missingRates || [];
    return (
        <>
            <Alert severity="info" variant="outlined" sx={{ mb: missing.length && isAdmin ? 1.5 : 3, borderColor: RULE, color: INK }}>
                Cost is estimated from the message rates, because Meta doesn't share cost for partner-billed accounts.
                Free messages cost nothing, and Meta's volume discounts aren't included.
                {isAdmin && estimate.metaCost !== undefined && (
                    <Box component="span" sx={{ display: 'block', mt: 0.75, fontWeight: 500 }}>
                        Meta's cost {formatINR(estimate.metaCost)}, your margin {formatINR(estimate.margin)}.
                    </Box>
                )}
            </Alert>
            {isAdmin && missing.length > 0 && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
                    No rate set for {missing.slice(0, 4).join(', ')}{missing.length > 4 ? ` and ${missing.length - 4} more` : ''}, so those messages count as ₹0.{' '}
                    <RouterLink to="/admin/rates">Set message rates</RouterLink>
                </Alert>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------
const MessagesTab = ({ data, granularity, animate }) => {
    const points = data?.analytics?.data_points || [];
    const totals = points.reduce(
        (acc, p) => ({ sent: acc.sent + (p.sent || 0), delivered: acc.delivered + (p.delivered || 0) }),
        { sent: 0, delivered: 0 }
    );
    const deliveryRate = totals.sent ? (totals.delivered / totals.sent) * 100 : 0;

    const chartData = points.map((p) => ({
        period: periodLabel(p.start, granularity),
        sent: p.sent || 0,
        delivered: p.delivered || 0
    }));

    if (!totals.sent && !totals.delivered) {
        return (
            <EmptyState title="No messages in this date range">
                No messages were sent from this account between the selected dates. Try a longer range, or check that the
                phone number is connected and sending.
            </EmptyState>
        );
    }

    return (
        <>
            <FigureStrip
                figures={[
                    { label: 'Messages sent', value: formatCount(totals.sent) },
                    { label: 'Delivered', value: formatCount(totals.delivered) },
                    {
                        label: 'Delivery rate',
                        value: formatPercent(deliveryRate),
                        meter: deliveryRate,
                        hint: `${formatCount(totals.sent - totals.delivered)} not delivered`
                    }
                ]}
            />

            <Section title="Sent and delivered over time">
                <Box sx={{ height: { xs: 240, md: 320 } }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={RULE} vertical={false} />
                            <XAxis dataKey="period" {...axisProps} minTickGap={16} />
                            <YAxis {...axisProps} axisLine={false} width={48} tickFormatter={formatCount} />
                            <Tooltip content={<ChartTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="sent"
                                name="Sent"
                                stroke={INK}
                                strokeWidth={2}
                                fill={INK}
                                fillOpacity={0.06}
                                isAnimationActive={animate}
                            />
                            <Area
                                type="monotone"
                                dataKey="delivered"
                                name="Delivered"
                                stroke="#1BAA52"
                                strokeWidth={2}
                                fill="#25D366"
                                fillOpacity={0.18}
                                isAnimationActive={animate}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, mt: 1.5 }}>
                    {[{ label: 'Sent', color: INK }, { label: 'Delivered', color: '#1BAA52' }].map((item) => (
                        <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 14, height: 3, borderRadius: 2, bgcolor: item.color }} />
                            <Typography variant="body2" sx={{ color: MUTED }}>{item.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Section>

            <DetailTable
                title="Breakdown by period"
                columns={[
                    { key: 'period', label: 'Period' },
                    { key: 'sent', label: 'Sent', numeric: true, render: (r) => formatCount(r.sent) },
                    { key: 'delivered', label: 'Delivered', numeric: true, render: (r) => formatCount(r.delivered) },
                    {
                        key: 'rate',
                        label: 'Delivery rate',
                        numeric: true,
                        render: (r) => (r.sent ? formatPercent((r.delivered / r.sent) * 100) : '–')
                    }
                ]}
                rows={points.map((p) => ({
                    period: periodLabel(p.start, granularity, true),
                    sent: p.sent || 0,
                    delivered: p.delivered || 0
                }))}
            />
        </>
    );
};

const CategoriesTab = ({ data, granularity, animate }) => {
    const analytics = data?.conversationAnalytics;
    const points = useMemo(() => analytics?.data?.[0]?.data_points || [], [analytics]);
    const costAvailable = analytics?.costAvailable !== false || !!analytics?.costEstimated;
    const costLabel = analytics?.costEstimated ? 'Estimated cost' : 'Approximate cost';

    const byCategory = useMemo(
        () => groupTotals(points, 'conversation_category', ['conversation', 'cost']).map((g) => ({
            key: g.key,
            label: categoryMeta(g.key).label,
            color: categoryMeta(g.key).color,
            value: g.conversation,
            cost: g.cost
        })),
        [points]
    );

    const categoryKeys = byCategory.map((c) => c.key);

    const chartData = useMemo(() => {
        const rows = {};
        points.forEach((p) => {
            if (!rows[p.start]) rows[p.start] = { start: p.start, period: periodLabel(p.start, granularity) };
            const key = p.conversation_category || 'UNKNOWN';
            rows[p.start][key] = (rows[p.start][key] || 0) + (p.conversation || 0);
        });
        return Object.values(rows).sort((a, b) => a.start - b.start);
    }, [points, granularity]);

    const totalMessages = byCategory.reduce((sum, c) => sum + c.value, 0);
    const totalCost = byCategory.reduce((sum, c) => sum + c.cost, 0);
    const paidMessages = points
        .filter((p) => (p.conversation_type || 'REGULAR') === 'REGULAR')
        .reduce((sum, p) => sum + (p.conversation || 0), 0);

    if (!totalMessages) {
        return (
            <>
                <CostNotice analytics={analytics} />
                <EmptyState title="No billable message data in this date range">
                    {analytics?.message || 'Meta has no message category data for these dates.'} Try a longer range.
                </EmptyState>
            </>
        );
    }

    return (
        <>
            <CostNotice analytics={analytics} />

            <FigureStrip
                figures={[
                    { label: 'Messages delivered', value: formatCount(totalMessages) },
                    {
                        label: 'Paid messages',
                        value: formatCount(paidMessages),
                        hint: `${formatCount(totalMessages - paidMessages)} free`
                    },
                    costAvailable
                        ? { label: costLabel, value: formatINR(totalCost) }
                        : { label: costLabel, value: 'Not available', unavailable: true }
                ]}
            />

            <Section
                title="What kind of messages you sent"
                description="Meta charges per delivered message, and the rate depends on its category."
            >
                <CompositionBar items={byCategory} valueLabel="messages" showCost={costAvailable} />
            </Section>

            <Section title="Categories over time">
                <Box sx={{ height: { xs: 240, md: 300 } }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={RULE} vertical={false} />
                            <XAxis dataKey="period" {...axisProps} minTickGap={16} />
                            <YAxis {...axisProps} axisLine={false} width={48} tickFormatter={formatCount} />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(31, 42, 61, 0.04)' }} />
                            {categoryKeys.map((key, index) => (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    name={categoryMeta(key).label}
                                    stackId="categories"
                                    fill={categoryMeta(key).color}
                                    maxBarSize={36}
                                    radius={index === categoryKeys.length - 1 ? [3, 3, 0, 0] : 0}
                                    isAnimationActive={animate}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Section>

            <DetailTable
                title="Breakdown by period"
                columns={[
                    { key: 'period', label: 'Period' },
                    {
                        key: 'category',
                        label: 'Category',
                        render: (r) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: categoryMeta(r.category).color }} />
                                {categoryMeta(r.category).label}
                            </Box>
                        )
                    },
                    { key: 'type', label: 'Pricing', render: (r) => pricingTypeMeta(r.type).label },
                    { key: 'messages', label: 'Messages', numeric: true, render: (r) => formatCount(r.messages) },
                    ...(costAvailable
                        ? [{ key: 'cost', label: 'Cost', numeric: true, render: (r) => formatINR(r.cost) }]
                        : [])
                ]}
                rows={[...points]
                    .sort((a, b) => a.start - b.start)
                    .map((p) => ({
                        period: periodLabel(p.start, granularity, true),
                        category: p.conversation_category || 'UNKNOWN',
                        type: p.conversation_type || 'REGULAR',
                        messages: p.conversation || 0,
                        cost: p.cost || 0
                    }))}
            />
        </>
    );
};

const RankedList = ({ items, showCost }) => {
    const max = Math.max(...items.map((i) => i.value), 1);
    return (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {items.map((item) => (
                <Box component="li" key={item.key} sx={{ py: 1.25, borderTop: `1px solid ${RULE}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                        <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }} noWrap>{item.label}</Typography>
                        <Typography variant="body2" sx={{ color: INK, flexShrink: 0 }}>
                            {formatCount(item.value)}
                            {showCost && (
                                <Box component="span" sx={{ color: MUTED, ml: 1.5 }}>{formatINR(item.cost)}</Box>
                            )}
                        </Typography>
                    </Box>
                    <Box sx={{ height: 6, bgcolor: RULE, borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${(item.value / max) * 100}%`, height: '100%', bgcolor: item.color || INK, borderRadius: 3 }} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const PricingTab = ({ data, granularity, animate }) => {
    const analytics = data?.pricingAnalytics;
    const points = useMemo(() => analytics?.data?.[0]?.data_points || [], [analytics]);
    const costAvailable = analytics?.costAvailable !== false || !!analytics?.costEstimated;
    const costLabel = analytics?.costEstimated ? 'Estimated cost' : 'Approximate cost';

    const byType = groupTotals(points, 'pricing_type', ['volume', 'cost']).map((g) => ({
        key: g.key,
        label: pricingTypeMeta(g.key).label,
        color: pricingTypeMeta(g.key).color,
        value: g.volume,
        cost: g.cost
    }));
    const byCountry = groupTotals(points, 'country', ['volume', 'cost'])
        .map((g) => ({ key: g.key, label: g.key === 'UNKNOWN' ? 'Unknown' : g.key, value: g.volume, cost: g.cost, color: '#0E7C86' }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);
    const byPhone = groupTotals(points, 'phone_number', ['volume', 'cost'])
        .map((g) => ({ key: g.key, label: g.key === 'UNKNOWN' ? 'Unknown number' : g.key, value: g.volume, cost: g.cost, color: INK }))
        .sort((a, b) => b.value - a.value);

    const chartData = useMemo(() => {
        const rows = {};
        points.forEach((p) => {
            if (!rows[p.start]) rows[p.start] = { start: p.start, period: periodLabel(p.start, granularity), paid: 0, free: 0, cost: 0 };
            if ((p.pricing_type || 'REGULAR') === 'REGULAR') rows[p.start].paid += p.volume || 0;
            else rows[p.start].free += p.volume || 0;
            rows[p.start].cost += p.cost || 0;
        });
        return Object.values(rows).sort((a, b) => a.start - b.start);
    }, [points, granularity]);

    const total = byType.reduce((sum, t) => sum + t.value, 0);
    const paid = byType.filter((t) => t.key === 'REGULAR').reduce((sum, t) => sum + t.value, 0);
    const totalCost = byType.reduce((sum, t) => sum + t.cost, 0);

    if (!total) {
        return (
            <>
                <CostNotice analytics={analytics} />
                <EmptyState title="No pricing data in this date range">
                    Meta reports pricing only for delivered messages. Try a longer range.
                </EmptyState>
            </>
        );
    }

    return (
        <>
            <CostNotice analytics={analytics} />

            <FigureStrip
                figures={[
                    { label: 'Paid messages', value: formatCount(paid) },
                    { label: 'Free messages', value: formatCount(total - paid), hint: `${formatPercent(((total - paid) / total) * 100)} of all delivered` },
                    costAvailable
                        ? { label: costLabel, value: formatINR(totalCost), hint: paid ? `${formatINR(totalCost / paid)} per paid message` : undefined }
                        : { label: costLabel, value: 'Not available', unavailable: true }
                ]}
            />

            <Section
                title="Paid and free messages"
                description="Replies inside the 24-hour customer service window and messages started from an ad or entry point are free."
            >
                <CompositionBar items={byType} valueLabel="messages" showCost={costAvailable} />
            </Section>

            <Section title={costAvailable ? 'Cost over time' : 'Paid and free over time'}>
                <Box sx={{ height: { xs: 240, md: 280 } }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {costAvailable ? (
                            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={RULE} vertical={false} />
                                <XAxis dataKey="period" {...axisProps} minTickGap={16} />
                                <YAxis {...axisProps} axisLine={false} width={64} tickFormatter={(v) => `₹${formatCount(v)}`} />
                                <Tooltip content={<ChartTooltip formatter={formatINR} />} />
                                <Area type="monotone" dataKey="cost" name="Cost" stroke={INK} strokeWidth={2} fill={INK} fillOpacity={0.08} isAnimationActive={animate} />
                            </AreaChart>
                        ) : (
                            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke={RULE} vertical={false} />
                                <XAxis dataKey="period" {...axisProps} minTickGap={16} />
                                <YAxis {...axisProps} axisLine={false} width={48} tickFormatter={formatCount} />
                                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(31, 42, 61, 0.04)' }} />
                                <Bar dataKey="paid" name="Paid" stackId="t" fill={INK} maxBarSize={36} isAnimationActive={animate} />
                                <Bar dataKey="free" name="Free" stackId="t" fill="#7FD8A4" maxBarSize={36} radius={[3, 3, 0, 0]} isAnimationActive={animate} />
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </Box>
            </Section>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 3 }}>
                <Section title="By country">
                    <RankedList items={byCountry} showCost={costAvailable} />
                </Section>
                <Section title="By phone number">
                    <RankedList items={byPhone} showCost={costAvailable} />
                </Section>
            </Box>
        </>
    );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const WhatsAppAnalytics = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [loadingAccount, setLoadingAccount] = useState(true);
    const [error, setError] = useState(null);

    const [range, setRange] = useState(() => presetRange(PRESETS[0]));
    const [granularity, setGranularity] = useState('DAY');
    const [activeTab, setActiveTab] = useState(0);

    const [tabData, setTabData] = useState({});
    const [loadingTab, setLoadingTab] = useState(false);
    const loadedKeys = useRef({});
    const animate = useMemo(() => !prefersReducedMotion(), []);

    useEffect(() => {
        const fetchAccount = async () => {
            try {
                setLoadingAccount(true);
                const response = await whatsappAccountService.getAccount(id);
                if (response.success && response.data) setAccount(response.data);
            } catch (err) {
                setError(getErrorMessage(err.response?.data?.error) || 'Could not load this WhatsApp account.');
            } finally {
                setLoadingAccount(false);
            }
        };
        fetchAccount();
    }, [id]);

    const loadTab = useCallback(async (tabIndex, force = false) => {
        const params = {
            startDate: Math.floor(range.start / 1000),
            endDate: Math.floor(range.end / 1000),
            granularity
        };
        const key = `${id}:${params.startDate}:${params.endDate}:${granularity}`;
        if (!force && loadedKeys.current[tabIndex] === key) return;

        const loaders = [
            whatsappAccountService.getAccountAnalytics,
            whatsappAccountService.getConversationAnalytics,
            whatsappAccountService.getPricingAnalytics
        ];

        try {
            setLoadingTab(true);
            setError(null);
            const response = await loaders[tabIndex](id, params);
            if (response.success && response.data) {
                loadedKeys.current[tabIndex] = key;
                setTabData((prev) => ({ ...prev, [tabIndex]: response.data }));
            } else {
                setError('Meta returned no analytics for this account.');
            }
        } catch (err) {
            // A 404 from the messages endpoint means Meta has no data points for the range
            if (err.response?.status === 404 && tabIndex === 0) {
                loadedKeys.current[tabIndex] = key;
                setTabData((prev) => ({ ...prev, [tabIndex]: null }));
            } else {
                setError(getErrorMessage(err.response?.data?.error) || 'Could not load analytics. Try again.');
            }
        } finally {
            setLoadingTab(false);
        }
    }, [id, range, granularity]);

    useEffect(() => {
        loadTab(activeTab);
    }, [activeTab, loadTab]);

    const applyPreset = (key) => {
        const preset = PRESETS.find((p) => p.key === key);
        if (preset) setRange(presetRange(preset));
    };

    const activePreset = PRESETS.find((p) => {
        const r = presetRange(p);
        return range.start === r.start && range.end === r.end;
    })?.key;

    const handleDateChange = (field) => (event) => {
        if (!event.target.value) return;
        const value = fromInputValue(event.target.value);
        setRange((prev) => {
            const next = field === 'start' ? { ...prev, start: startOfDay(value) } : { ...prev, end: endOfDay(value) };
            return next.start > next.end ? prev : next;
        });
    };

    // ---- CSV export
    const exportCSV = () => {
        const rowsByTab = {
            0: () => (tabData[0]?.analytics?.data_points || []).map((p) => ({
                Period: periodLabel(p.start, granularity, true),
                Sent: p.sent || 0,
                Delivered: p.delivered || 0,
                'Delivery rate (%)': p.sent ? ((p.delivered / p.sent) * 100).toFixed(2) : ''
            })),
            1: () => (tabData[1]?.conversationAnalytics?.data?.[0]?.data_points || []).map((p) => ({
                Period: periodLabel(p.start, granularity, true),
                Category: categoryMeta(p.conversation_category || 'UNKNOWN').label,
                Pricing: pricingTypeMeta(p.conversation_type || 'REGULAR').label,
                Messages: p.conversation || 0,
                ...(tabData[1]?.conversationAnalytics?.costAvailable !== false || tabData[1]?.conversationAnalytics?.costEstimated
                    ? { [tabData[1]?.conversationAnalytics?.costEstimated ? 'Estimated cost (INR)' : 'Cost (INR)']: p.cost || 0 }
                    : {}),
                ...(p.metaCost !== undefined ? { 'Meta cost (INR)': p.metaCost } : {})
            })),
            2: () => (tabData[2]?.pricingAnalytics?.data?.[0]?.data_points || []).map((p) => ({
                Period: periodLabel(p.start, granularity, true),
                'Phone number': p.phone_number || '',
                Country: p.country || '',
                Category: categoryMeta(p.pricing_category || 'UNKNOWN').label,
                Pricing: pricingTypeMeta(p.pricing_type || 'REGULAR').label,
                Messages: p.volume || 0,
                ...(tabData[2]?.pricingAnalytics?.costAvailable !== false || tabData[2]?.pricingAnalytics?.costEstimated
                    ? { [tabData[2]?.pricingAnalytics?.costEstimated ? 'Estimated cost (INR)' : 'Cost (INR)']: p.cost || 0 }
                    : {}),
                ...(p.metaCost !== undefined ? { 'Meta cost (INR)': p.metaCost } : {})
            }))
        };
        const rows = rowsByTab[activeTab]();
        if (!rows.length) return;

        const headers = Object.keys(rows[0]);
        const escape = (value) => {
            const text = String(value ?? '');
            return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        };
        const csv = [headers.join(','), ...rows.map((row) => headers.map((h) => escape(row[h])).join(','))].join('\n');

        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        link.download = `whatsapp_${TABS[activeTab]}_${account?.businessName || 'account'}_${toInputValue(range.start)}_to_${toInputValue(range.end)}.csv`
            .replace(/\s+/g, '_');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const currentData = tabData[activeTab];
    const tabLoaded = loadedKeys.current[activeTab] !== undefined;

    return (
        <AppLayout>
            <Container maxWidth="lg" sx={{ pb: 6 }}>
                {/* Account header */}
                <Box sx={{ mb: 3 }}>
                    <Link
                        component="button"
                        onClick={() => navigate('/whatsapp-accounts')}
                        underline="hover"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: MUTED, fontSize: '0.875rem', mb: 1.5 }}
                    >
                        <ArrowBackIcon sx={{ fontSize: 16 }} />
                        WhatsApp accounts
                    </Link>
                    <Typography
                        component="h1"
                        sx={{ fontSize: { xs: '1.625rem', md: '2rem' }, fontWeight: 700, letterSpacing: '-0.02em', color: INK, lineHeight: 1.2 }}
                    >
                        {loadingAccount ? <Skeleton width={280} /> : account?.verifiedName || account?.businessName || 'WhatsApp account'}
                    </Typography>
                    {account && (
                        <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                            {account.phoneNumber}
                            {account.wabaId && (
                                <Box component="span" sx={{ ml: 2 }}>Business account ID {account.wabaId}</Box>
                            )}
                        </Typography>
                    )}
                </Box>

                {/* Controls */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: { xs: 1.5, md: 2 },
                        mb: 2
                    }}
                >
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={activePreset || null}
                        onChange={(e, key) => key && applyPreset(key)}
                        aria-label="Date range presets"
                    >
                        {PRESETS.map((preset) => (
                            <ToggleButton key={preset.key} value={preset.key} sx={{ px: 1.5, textTransform: 'none' }}>
                                {preset.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            type="date"
                            size="small"
                            label="From"
                            value={toInputValue(range.start)}
                            onChange={handleDateChange('start')}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: toInputValue(range.end) }}
                            sx={{ width: 160 }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="To"
                            value={toInputValue(range.end)}
                            onChange={handleDateChange('end')}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: toInputValue(range.start), max: toInputValue(Date.now()) }}
                            sx={{ width: 160 }}
                        />
                    </Box>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={granularity}
                        onChange={(e, value) => value && setGranularity(value)}
                        aria-label="Group by"
                    >
                        <ToggleButton value="DAY" sx={{ px: 1.5, textTransform: 'none' }}>Daily</ToggleButton>
                        <ToggleButton value="MONTH" sx={{ px: 1.5, textTransform: 'none' }}>Monthly</ToggleButton>
                    </ToggleButtonGroup>

                    <Box sx={{ flexGrow: 1 }} />

                    <MuiTooltip title="Reload from Meta">
                        <span>
                            <IconButton onClick={() => loadTab(activeTab, true)} disabled={loadingTab} aria-label="Reload analytics">
                                {loadingTab ? <CircularProgress size={20} /> : <RefreshIcon />}
                            </IconButton>
                        </span>
                    </MuiTooltip>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={exportCSV}
                        disabled={!currentData || loadingTab}
                        sx={{ textTransform: 'none' }}
                    >
                        Export CSV
                    </Button>
                </Box>

                <Tabs
                    value={activeTab}
                    onChange={(e, value) => setActiveTab(value)}
                    sx={{ mb: 3, borderBottom: `1px solid ${RULE}`, '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minWidth: 0, px: 0, mr: 3 } }}
                    variant="scrollable"
                    allowScrollButtonsMobile
                >
                    <Tab label="Messages" />
                    <Tab label="Categories" />
                    <Tab label="Pricing" />
                </Tabs>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loadingTab && !tabLoaded ? (
                    <LoadingState />
                ) : (
                    <Box sx={{ opacity: loadingTab ? 0.55 : 1, transition: 'opacity 150ms' }} aria-busy={loadingTab}>
                        {activeTab === 0 && tabLoaded && (
                            <MessagesTab data={currentData} granularity={granularity} animate={animate} />
                        )}
                        {activeTab === 1 && tabLoaded && (
                            <CategoriesTab data={currentData} granularity={granularity} animate={animate} />
                        )}
                        {activeTab === 2 && tabLoaded && (
                            <PricingTab data={currentData} granularity={granularity} animate={animate} />
                        )}
                    </Box>
                )}
            </Container>
        </AppLayout>
    );
};

export default WhatsAppAnalytics;
